import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getConsultantCalendar, getConsultantDays } from '../../api/reportsApi';
import { getWorkItemAudit, readServerError } from '../../api/deliveryApi';
import WorkItemDrawer from '../Projects/Delivery/WorkItemDrawer';
import { useToast } from '../Common/ToastProvider';
import {
  DOW_ABBR, combinationClass, fmtDdMm, fmtHours, fmtIso,
  isWeekend, navStep, parseIso, periodLabel, rangeOf,
} from './calendarUtils';
import './ConsultantTimesheetPanel.css';

// TC-S2 — Docked consultant timesheet panel. Renders three views (m/w/d)
// scoped to a single consultant using the /consultant-calendar and
// /consultant-days endpoints. Reference:
// docs/presentations/hybrid-pm-concept/team-calendar-v2.html panel 2.
//
// Task titles render as PLAIN TEXT in this slice; the WI drawer comes
// in TC-S3 and is deliberately NOT wired here.

const KIND_ORDER = { Task: 0, Project: 1, ShortLeave: 2, Leave: 3 };

const chipFor = (entry) => {
  if (entry.kind === 'Leave' || entry.kind === 'ShortLeave') return { cls: 'lv', label: 'Leave' };
  return entry.isBillable ? { cls: 'b', label: 'Billable' } : { cls: 'nb', label: 'Non-billable' };
};

// Split a "yyyy-MM-dd" (server) back to a local Date once, so the
// panel's date math stays deterministic across renders.
const dt = (s) => parseIso(s);

export default function ConsultantTimesheetPanel({
  employeeId, name, initialDate, onClose,
}) {
  const toast = useToast();
  const [view, setView] = useState('d');
  const [anchor, setAnchor] = useState(() => (initialDate ? new Date(initialDate) : new Date()));
  const [dockMax, setDockMax] = useState(false);
  const [monthData, setMonthData] = useState(null);
  const [daysData, setDaysData] = useState(null);
  const [loading, setLoading] = useState(false);
  // TC-S3.1 — stale-while-revalidate. ◀/▶ within the same view keeps
  // the current table visible and only dims it via .tc-refreshing;
  // a view switch (m ↔ w ↔ d) clears both buffers and shows the full
  // Loading placeholder because a different table is genuinely coming.
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  // TC-S3 — audit drawer state. `loadingId` tracks the row we're
  // fetching so the caller can render an inline "…" affordance while
  // /work-items/{id}/audit is in flight.
  const [audit, setAudit] = useState(null);
  const [auditLoadingId, setAuditLoadingId] = useState(null);
  const openAudit = useCallback(async (workItemId) => {
    if (!workItemId) return;
    setAuditLoadingId(workItemId);
    try {
      const dto = await getWorkItemAudit(workItemId);
      setAudit(dto);
    } catch (err) {
      toast?.(readServerError(err), { variant: 'error' });
    } finally {
      setAuditLoadingId(null);
    }
  }, [toast]);

  const range = useMemo(() => rangeOf(view, anchor), [view, anchor]);

  const load = useCallback(async () => {
    // TC-S3.1 — pick the loading mode based on whether the buffer
    // for the CURRENT view is already populated. First load or view
    // switch (both wipe the buffers) → full Loading placeholder.
    // ◀/▶ within the same view → dim overlay only.
    const havePrev = view === 'm' ? monthData != null : daysData != null;
    if (havePrev) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      if (view === 'm') {
        const dto = await getConsultantCalendar(employeeId, fmtIso(range.from), fmtIso(range.to));
        setMonthData(dto);
        setDaysData(null);
      } else {
        // Week and day both use /consultant-days.
        const dto = await getConsultantDays(employeeId, fmtIso(range.from), fmtIso(range.to));
        setDaysData(dto);
        setMonthData(null);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load consultant data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // monthData/daysData in the deps would re-trigger the fetch on
    // each setState; the callback only reads them synchronously to
    // pick the loading mode, so the eslint hint is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, view, range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  // Bubble the current anchor date up to the parent so the team
  // grid can mark the .tc-sel cell on the same day the panel is on.
  const onDayClick = (isoDate) => {
    setView('d');
    setAnchor(parseIso(isoDate));
  };

  const cellDate = (view === 'd') ? fmtIso(anchor) : null;

  // ─── Month view (7-col personal calendar). ───────────────────────────
  const renderMonth = () => {
    const days = (monthData?.consultant?.days) || [];
    if (days.length === 0) return null;
    const first = dt(days[0].date);
    // Monday of the first partial week (leading blanks fill in).
    const lead = (first.getDay() + 6) % 7;
    const cells = Array.from({ length: lead }).map(() => null).concat(days);
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let r = 0; r < cells.length / 7; r++) rows.push(cells.slice(r * 7, r * 7 + 7));
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return (
      <table className="ct-cal" data-testid="ct-cal">
        <thead>
          <tr>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <th key={d}>{d}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((week, ri) => (
            <tr key={ri}>
              {week.map((day, ci) => {
                if (!day) return <td key={ci} className="ct-oth" />;
                const d = dt(day.date);
                const b = Number(day.billableHours) || 0;
                const n = Number(day.nonBillableHours) || 0;
                const l = Number(day.leaveHours) || 0;
                const sum = b + n + l;
                const future = d > today;
                const we = isWeekend(d) || day.type === 'Weekend';
                const holiday = day.type === 'Holiday';
                const gap = !we && !holiday && !future && sum === 0;
                const cls = combinationClass(day);
                const parts = [];
                if (b > 0) parts.push(`${fmtHours(b)} bil.`);
                if (n > 0) parts.push(`${fmtHours(n)} n-bil.`);
                if (l > 0) parts.push(day.type === 'PaidLeave' || day.type === 'SickLeave' ? 'leave' : `${fmtHours(l)} leave`);
                return (
                  <td key={day.date} className={we ? 'ct-oth' : ''}>
                    <span
                      className={`ct-dc ${cls}`}
                      data-testid={`ct-day-${day.date}`}
                      onClick={we || future ? undefined : () => onDayClick(day.date)}
                      style={we || future ? { cursor: 'default' } : undefined}
                    >
                      <span className="ct-dn">{d.getDate()}</span>
                      {!we && !future && (
                        <>
                          <div className="ct-dh">
                            {sum > 0 ? fmtHours(sum) : (gap ? <span className="ct-gap">!</span> : '·')}
                          </div>
                          {parts.length > 0 && <div className="ct-dt">{parts.join(' + ')}</div>}
                        </>
                      )}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // ─── Week view (task/project rows × Mon-Fri columns). ───────────────
  const renderWeek = () => {
    const days = (daysData?.days) || [];
    if (days.length === 0) return null;
    // Aggregate: one row per Task (grouped by WorkItemId), then one
    // row per Project (grouped by ProjectName), then one Leave row if
    // any day carries an absence.
    const taskRows = new Map();
    const projectRows = new Map();
    const leaveByDay = {};
    days.forEach((d) => {
      d.entries.forEach((e) => {
        if (e.kind === 'Task') {
          const key = e.workItemId;
          if (!taskRows.has(key)) {
            taskRows.set(key, {
              workItemId: e.workItemId,
              workItemTitle: e.workItemTitle,
              workItemStatus: e.workItemStatus,
              projectName: e.projectName,
              byDate: {},
            });
          }
          taskRows.get(key).byDate[d.date] = (taskRows.get(key).byDate[d.date] || 0) + Number(e.hours || 0);
        } else if (e.kind === 'Project') {
          const key = e.projectName || '—';
          if (!projectRows.has(key)) {
            projectRows.set(key, {
              projectName: key, notes: {}, byDate: {},
            });
          }
          const r = projectRows.get(key);
          r.byDate[d.date] = (r.byDate[d.date] || 0) + Number(e.hours || 0);
          if (e.note) r.notes[d.date] = e.note;
        } else if (e.kind === 'ShortLeave' || e.kind === 'Leave') {
          leaveByDay[d.date] = (leaveByDay[d.date] || 0) + Number(e.hours || 0);
        }
      });
    });
    const dayTotals = days.map((d) => Number(d.totalHours || 0));
    return (
      <table className="ct-g" data-testid="ct-week">
        <colgroup>
          <col style={{ width: 340 }} />
          <col style={{ width: 170 }} />
          {days.map((_, i) => <col key={i} />)}
          <col style={{ width: 64 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="ct-l">Task / activity</th>
            <th className="ct-l">Project</th>
            {days.map((d) => {
              const dd = dt(d.date);
              return <th key={d.date}>{DOW_ABBR[dd.getDay()]} {fmtDdMm(dd)}</th>;
            })}
            <th>Σ</th>
          </tr>
        </thead>
        <tbody>
          {Array.from(taskRows.values()).map((r) => {
            const total = Object.values(r.byDate).reduce((a, b) => a + b, 0);
            return (
              <tr key={`t-${r.workItemId}`} data-testid={`ct-week-task-${r.workItemId}`}>
                <td className="ct-l">
                  <span className={`ct-mi ${r.workItemStatus === 'Closed' ? 'C' : r.workItemStatus === 'Active' ? 'A' : 'N'}`}>
                    {r.workItemStatus === 'Closed' ? 'C' : r.workItemStatus === 'Active' ? 'A' : 'N'}
                  </span>
                  WI-{r.workItemId} {r.workItemTitle}
                </td>
                <td className="ct-l">{r.projectName || '—'}</td>
                {days.map((d) => {
                  const v = r.byDate[d.date];
                  return (
                    <td key={d.date}>
                      <span
                        className={`ct-cell ${v ? 'tc-b' : ''}`}
                        onClick={() => onDayClick(d.date)}
                      >{v ? fmtHours(v) : '·'}</span>
                    </td>
                  );
                })}
                <td className="ct-n"><b>{fmtHours(total)}</b></td>
              </tr>
            );
          })}
          {Array.from(projectRows.values()).map((r) => {
            const total = Object.values(r.byDate).reduce((a, b) => a + b, 0);
            return (
              <tr key={`p-${r.projectName}`} data-testid={`ct-week-project-${r.projectName}`}>
                <td className="ct-l ct-note">
                  <div>{r.projectName}</div>
                  {Object.keys(r.notes).length > 0 && (
                    <div className="ct-nbnote">📝 notes per day — hover a cell or open day view</div>
                  )}
                </td>
                <td />
                {days.map((d) => {
                  const v = r.byDate[d.date];
                  const nt = r.notes[d.date];
                  return (
                    <td key={d.date}>
                      <span
                        className={`ct-cell ${v ? 'tc-nb' : ''}`}
                        title={nt ? `📝 ${nt}` : undefined}
                        onClick={() => onDayClick(d.date)}
                      >{v ? fmtHours(v) : '·'}</span>
                    </td>
                  );
                })}
                <td className="ct-n">{fmtHours(total)}</td>
              </tr>
            );
          })}
          {Object.keys(leaveByDay).length > 0 && (
            <tr data-testid="ct-week-leave">
              <td className="ct-l">Absence</td>
              <td />
              {days.map((d) => {
                const v = leaveByDay[d.date];
                return (
                  <td key={d.date}>
                    <span
                      className={`ct-cell ${v ? 'tc-lv' : ''}`}
                      onClick={() => onDayClick(d.date)}
                    >{v ? fmtHours(v) : '·'}</span>
                  </td>
                );
              })}
              <td className="ct-n">
                {fmtHours(Object.values(leaveByDay).reduce((a, b) => a + b, 0))}
              </td>
            </tr>
          )}
          <tr className="ct-sum">
            <td className="ct-l">Daily total</td>
            <td />
            {dayTotals.map((t, i) => (
              <td key={i} className="ct-n" style={{ textAlign: 'center' }}>
                {t > 0 ? fmtHours(t) : '·'}
              </td>
            ))}
            <td className="ct-n"><b>{fmtHours(dayTotals.reduce((a, b) => a + b, 0))}</b></td>
          </tr>
        </tbody>
      </table>
    );
  };

  // ─── Day view (typed entries). ──────────────────────────────────────
  const renderDay = () => {
    const day = (daysData?.days || [])[0];
    if (!day) return null;
    const type = day.type;
    if (day.entries.length === 0) {
      if (type === 'Weekend' || type === 'Holiday' || type === 'Left') {   // CEV-S3 — 'Left': after the leaving date
        return <div className="ct-empty ct-weekend">{type === 'Weekend' ? 'weekend' : type === 'Left' ? 'left' : 'holiday'}</div>;
      }
      return <div className="ct-empty ct-missing">working day without logged hours</div>;
    }
    const entries = [...day.entries].sort((a, b) => (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9));
    return (
      <table className="ct-g" data-testid="ct-day">
        <colgroup>
          <col style={{ width: 420 }} />
          <col />
          <col style={{ width: 150 }} />
          <col style={{ width: 90 }} />
        </colgroup>
        <thead>
          <tr>
            <th className="ct-l">Task / activity</th>
            <th className="ct-l">Project · theme</th>
            <th>Type</th>
            <th>Hours</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => {
            const chip = chipFor(e);
            let primary; let projectCell;
            if (e.kind === 'Task') {
              const st = e.workItemStatus === 'Closed' ? 'C' : e.workItemStatus === 'Active' ? 'A' : 'N';
              const loading = auditLoadingId === e.workItemId;
              primary = (
                <>
                  <span className={`ct-mi ${st}`}>{st}</span>
                  <button
                    type="button"
                    className="ct-dl"
                    data-testid={`ct-task-link-${e.workItemId}`}
                    onClick={() => openAudit(e.workItemId)}
                    style={{
                      color: '#2a5db0', cursor: 'pointer',
                      background: 'transparent', border: 'none',
                      padding: 0, textAlign: 'left', font: 'inherit',
                    }}
                    onMouseOver={(ev) => { ev.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseOut={(ev) => { ev.currentTarget.style.textDecoration = 'none'; }}
                  >
                    WI-{e.workItemId} {e.workItemTitle}
                  </button>
                  {loading && <span style={{ marginLeft: 6, color: '#999' }}>…</span>}
                </>
              );
              projectCell = (
                <>
                  {e.projectName}{e.taskTitle ? ` · ${e.taskTitle}` : ''}
                </>
              );
            } else if (e.kind === 'Project') {
              primary = (
                <>
                  <div>{e.projectName}</div>
                  {e.note && <div className="ct-nbnote">📝 {e.note}</div>}
                </>
              );
              projectCell = '';
            } else if (e.kind === 'ShortLeave') {
              primary = (
                <>
                  <div>Short absence{e.interval ? ` ${e.interval}` : ''}</div>
                  {e.note && <div className="ct-nbnote">📝 {e.note}</div>}
                </>
              );
              projectCell = '';
            } else { // Leave
              primary = type === 'SickLeave' ? 'Sick leave' : 'Paid leave';
              projectCell = '';
            }
            return (
              <tr key={i} data-testid={`ct-day-entry-${i}`}>
                <td className="ct-l ct-note">{primary}</td>
                <td className="ct-l">{projectCell}</td>
                <td><span className={`ct-chip ${chip.cls}`}>{chip.label}</span></td>
                <td className="ct-n"><b>{fmtHours(e.hours)}</b></td>
              </tr>
            );
          })}
          <tr className="ct-sum">
            <td className="ct-l">Total</td>
            <td /><td />
            <td className="ct-n"><b>{fmtHours(day.totalHours)}</b></td>
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <div
      className={`ct-dock${dockMax ? ' max' : ''}`}
      data-testid="ct-panel"
      data-employee-id={employeeId}
      data-anchor-date={cellDate || ''}
    >
      <div className="ct-ph">
        <div className="ct-tt">{name}</div>
        <div className="ct-ts">timesheet · opened from the map</div>
        <div className="ct-sp" />
        <div className="ct-ctl">
          <div className="ct-seg">
            {/* TC-S3.1 — clear both buffers so the next load uses the
                full Loading placeholder (view switch = different table). */}
            <button type="button" className={view === 'm' ? 'on' : ''}
                    data-testid="ct-view-m"
                    onClick={() => { if (view !== 'm') { setMonthData(null); setDaysData(null); setView('m'); } }}>Month</button>
            <button type="button" className={view === 'w' ? 'on' : ''}
                    data-testid="ct-view-w"
                    onClick={() => { if (view !== 'w') { setMonthData(null); setDaysData(null); setView('w'); } }}>Week</button>
            <button type="button" className={view === 'd' ? 'on' : ''}
                    data-testid="ct-view-d"
                    onClick={() => { if (view !== 'd') { setMonthData(null); setDaysData(null); setView('d'); } }}>Day</button>
          </div>
          <span className="ct-arr" data-testid="ct-prev"
                onClick={() => setAnchor((a) => navStep(view, a, -1))}>◀</span>
          <span className="ct-plabel" data-testid="ct-period-label">{periodLabel(view, anchor)}</span>
          <span className="ct-arr" data-testid="ct-next"
                onClick={() => setAnchor((a) => navStep(view, a, 1))}>▶</span>
        </div>
        <span className="ct-x" data-testid="ct-dock-toggle"
              title={dockMax ? 'Restore' : 'Expand'}
              onClick={() => setDockMax((v) => !v)}>{dockMax ? '⌄' : '⌃'}</span>
        <span className="ct-x" data-testid="ct-close" onClick={onClose} title="Close">✕</span>
      </div>

      <div className="ct-pb">
        {error && <div className="ct-error" data-testid="ct-error">{error}</div>}
        {loading ? (
          <div className="ct-empty" data-testid="ct-loading">Loading…</div>
        ) : (
          <div
            className={`ct-body${refreshing ? ' tc-refreshing' : ''}`}
            data-testid="ct-body"
          >
            {view === 'm' && renderMonth()}
            {view === 'w' && renderWeek()}
            {view === 'd' && renderDay()}
          </div>
        )}
      </div>

      {/* TC-S3 — Audit drawer opened when a task title is clicked.
          The drawer sits above the docked panel (its own z-index). ✕
          just clears the audit prop; the panel stays open. */}
      {audit && (
        <WorkItemDrawer
          open={!!audit}
          auditMode
          audit={audit}
          onClose={() => setAudit(null)}
        />
      )}
    </div>
  );
}
